import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Divider,
    Grid,
    Paper,
    Skeleton,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import HotelIcon from '@mui/icons-material/Hotel';
import { GuestService } from '../../../api/services/guestService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';

export default function GuestDetailsModal({ open, onClose, guestId, tenantId }) {
    const { currency } = useCurrency();
    const [detail, setDetail] = useState(null);
    const [stayHistory, setStayHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idImagePopup, setIdImagePopup] = useState(null);

    useEffect(() => {
        if (open && guestId) {
            fetchDetail(guestId);
        } else {
            setDetail(null);
            setStayHistory([]);
        }
    }, [open, guestId, tenantId]);

    const fetchDetail = async (id) => {
        setLoading(true);
        try {
            const [detailRes, historyRes] = await Promise.all([
                GuestService.getGuestById(id, tenantId),
                GuestService.getGuestStayHistory(id, tenantId).catch(() => ({ data: [] }))
            ]);
            setDetail(detailRes.data || detailRes);
            setStayHistory(historyRes.data || historyRes || []);
        } catch (error) {
            console.error('Failed to fetch guest details:', error);
            toast.error('Failed to load guest details.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.split('T')[0];
    };

    if (!open) return null;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold">Guest Profile Details</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />

                <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                    {loading || !detail ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} /></Grid>
                                <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} /></Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {/* Personal & Contact Information */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }} elevation={0}>
                                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold', display: 'block', letterSpacing: 1 }}>
                                        Personal Information
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 48, height: 48, mr: 2, fontWeight: 'bold' }}>
                                            {detail.name ? detail.name.charAt(0).toUpperCase() : 'G'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                                {(detail.title ? detail.title + ' ' : '') + (detail.name || 'Unknown Guest')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Guest Profile
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">Email</Typography>
                                            <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>{detail.email || 'N/A'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                            <Typography variant="body2" fontWeight="medium">{detail.phone || 'N/A'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Gender</Typography>
                                            <Typography variant="body2" fontWeight="medium">{detail.gender || 'N/A'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                                            <Typography variant="body2" fontWeight="medium">{formatDate(detail.birthDate)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Nationality</Typography>
                                            <Typography variant="body2" fontWeight="medium">{detail.nationality || 'N/A'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Birth Country</Typography>
                                            <Typography variant="body2" fontWeight="medium">{detail.birthCountry || 'N/A'}</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                                        <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, flex: 1, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }} elevation={0}>
                                            <HotelIcon color="primary" sx={{ opacity: 0.7 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold">Total Stays</Typography>
                                                <Typography variant="h6" color="text.primary" sx={{ lineHeight: 1 }}>{detail.totalStays ?? 0}</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, flex: 1, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }} elevation={0}>
                                            <EventIcon color="primary" sx={{ opacity: 0.7 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold">Last Stay</Typography>
                                                <Typography variant="body1" color="text.primary" fontWeight="medium" sx={{ lineHeight: 1.2 }}>{formatDate(detail.lastStayDate)}</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Address & Identity Documents */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column', gap: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }} elevation={0}>
                                    
                                    {/* Address Information */}
                                    <Box>
                                        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold', display: 'block', letterSpacing: 1 }}>
                                            Address Information
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Address</Typography>
                                                <Typography variant="body2" fontWeight="medium" align="right" sx={{ maxWidth: '60%' }}>
                                                    {detail.address || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">City</Typography>
                                                <Typography variant="body2" fontWeight="medium">{detail.city || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">State</Typography>
                                                <Typography variant="body2" fontWeight="medium">{detail.state || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Country</Typography>
                                                <Typography variant="body2" fontWeight="medium">{detail.country || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">ZIP</Typography>
                                                <Typography variant="body2" fontWeight="medium">{detail.zip || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Identity Documents */}
                                    <Box>
                                        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold', display: 'block', letterSpacing: 1 }}>
                                            Identity Documents
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">ID Type</Typography>
                                                <Chip size="small" label={detail.idType || 'N/A'} color="default" />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">ID Number</Typography>
                                                <Typography variant="body2" fontWeight="medium">{detail.idProof || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Issuing Country/City</Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {[detail.issuingCountry, detail.issuingCity].filter(Boolean).join(' / ') || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Expiry Date</Typography>
                                                <Typography variant="body2" fontWeight="medium">{formatDate(detail.idExpiryDate)}</Typography>
                                            </Box>
                                            {detail.idDocumentUrl && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Button size="small" variant="outlined" onClick={() => setIdImagePopup(detail.idDocumentUrl)} sx={{ textTransform: 'none', borderRadius: 2 }} fullWidth>
                                                        View Uploaded ID Document
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Stay History Table */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }} elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
                                            Stay History
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <TableContainer sx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Booking ID</TableCell>
                                                    <TableCell>Check-In</TableCell>
                                                    <TableCell>Check-Out</TableCell>
                                                    <TableCell>Room(s)</TableCell>
                                                    <TableCell>Total</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {stayHistory.length > 0 ? (
                                                    stayHistory.map((stay, index) => {
                                                        const rawId = stay.bookingCode || stay.reservationId || '';
                                                        const displayId = rawId.length > 8 ? rawId.substring(0, 8).toUpperCase() : rawId;
                                                        return (
                                                        <TableRow key={stay.reservationId || index} hover>
                                                            <TableCell>
                                                                <Tooltip title={rawId || 'N/A'}>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', bgcolor: 'rgba(0,0,0,0.04)', p: 0.5, borderRadius: 1, display: 'inline-block' }}>
                                                                        {displayId || 'N/A'}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell><Typography variant="body2" fontWeight="medium">{formatDate(stay.checkInDate)}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2" color="text.secondary">{formatDate(stay.checkOutDate)}</Typography></TableCell>
                                                            <TableCell>{stay.roomNumbers?.join(', ') || 'N/A'}</TableCell>
                                                            <TableCell><Typography variant="body2" fontWeight="medium">{stay.totalAmount != null ? formatCurrency(stay.totalAmount, currency) : 'N/A'}</Typography></TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    size="small" 
                                                                    label={stay.status || 'Completed'} 
                                                                    color={
                                                                        stay.status === 'Cancelled' ? 'error' : 
                                                                        stay.status === 'Checked-out' || stay.status === 'Completed' ? 'default' : 
                                                                        'primary'
                                                                    }
                                                                    sx={{ fontWeight: 'bold' }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )})
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                No stay history found for this guest.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>

            {/* ID Image Popup Dialog */}
            <Dialog open={!!idImagePopup} onClose={() => setIdImagePopup(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    View Uploaded ID
                    <IconButton onClick={() => setIdImagePopup(null)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                    {idImagePopup && (
                        <Box component="img" src={idImagePopup} alt="Uploaded ID" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
